import { mat4 } from 'gl-matrix'
import { BundleData } from './assets'
import { Layer, RenderItem, Renderer } from './renderer'
import { ProgramType, bindVertices, createProgram } from './shader'
import {
  itemShader,
  writeVerticesArc,
  writeVerticesBackground,
  writeVerticesCircle,
  writeVerticesImage,
  writeVerticesLine
} from './vertexWriter'

function createTexture(gl: WebGLRenderingContext, image: TexImageSource) {
  const texture = gl.createTexture()!
  gl.bindTexture(gl.TEXTURE_2D, texture)
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image)
  gl.generateMipmap(gl.TEXTURE_2D)
  //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
  gl.texParameteri(
    gl.TEXTURE_2D,
    gl.TEXTURE_MIN_FILTER,
    gl.LINEAR_MIPMAP_NEAREST
  )
  //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT)
  //gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT)
  return texture
}

type DrawBuffer = {
  layer: number
  shader: ReturnType<typeof itemShader>
  texture: WebGLTexture
  buffer: WebGLBuffer
  size: number
}

const imageSource = {
  vs: `
uniform mat4 uTransform;
attribute vec2 aPos;
attribute vec2 aTex;
attribute vec4 aColor;
attribute float aDesaturate;
varying vec2 vTex;
varying vec4 vColor;
varying float vDesaturate;
void main() {
  gl_Position = uTransform * vec4(aPos, 0.0, 1.0);
  vTex = aTex;
  vColor = aColor;
  vDesaturate = aDesaturate;
}
`,
  fs: `
precision mediump float;
varying vec2 vTex;
varying vec4 vColor;
varying float vDesaturate;
uniform sampler2D uTex;
void main() {
  vec4 tex = texture2D(uTex, vTex);
  float avg = tex.x * 0.3 + tex.y * 0.59 + tex.z * 0.11;
  vec3 sat = tex.xyz * (1.0 - vDesaturate) + vec3(avg, avg, avg) * vDesaturate;
  gl_FragColor = vec4(sat * vColor.xyz * (255.0 / 128.0), tex.w * vColor.w);
}
`,
  attributes: [
    ['aPos', 2, WebGLRenderingContext.FLOAT],
    ['aTex', 2, WebGLRenderingContext.FLOAT],
    ['aColor', 4, WebGLRenderingContext.UNSIGNED_BYTE],
    ['aDesaturate', 1, WebGLRenderingContext.FLOAT]
  ] as const,
  uniforms: ['uTransform', 'uTex'] as const
}

const backgroundSource = {
  vs: `
uniform mat4 uTransform;
uniform vec4 uDimensions;
uniform float uScale;
attribute vec2 aPos;
attribute float aScale;
varying vec2 vTex;
void main() {
  vec2 pos = uDimensions.xy + (uDimensions.zw - uDimensions.xy) * aPos;
  gl_Position = uTransform * vec4(pos, 0.0, 1.0);
  vTex = (pos - uDimensions.xy) / aScale;
}
`,
  fs: `
precision mediump float;
varying vec2 vTex;
uniform sampler2D uTex;
void main() {
  gl_FragColor = texture2D(uTex, vTex);
}
`,
  attributes: [
    ['aPos', 2, WebGLRenderingContext.FLOAT],
    ['aScale', 1, WebGLRenderingContext.FLOAT]
  ] as const,
  uniforms: ['uTransform', 'uDimensions', 'uTex'] as const
}

const lineMaxScale = 0.15
const lineHeight = '1.0'

const lineSource = {
  vs: `
uniform mat4 uTransform;
uniform float uScale;
attribute vec2 aPos;
attribute vec2 aOffset;
attribute vec2 aTex;
attribute float aHeight;
attribute vec4 aColor;
attribute vec4 aFlatColor;
varying vec2 vTex;
varying vec4 vColor;
varying float vHeight;
void main() {
  float height = uScale > ${lineMaxScale} ? aHeight : sign(aHeight) * 2.0 / uScale;
  gl_Position = uTransform * vec4(aPos + aOffset * height, 0.0, 1.0);
  vTex = aTex;
  vColor = uScale > ${lineMaxScale} ? aColor : vec4(aFlatColor.xyz * aColor.xyz, aColor.w);
  vHeight = height;
}
`,
  fs: `
precision mediump float;
uniform highp float uScale;
varying vec2 vTex;
varying vec4 vColor;
varying float vHeight;
uniform sampler2D uTex;
void main() {
  vec4 color = vec4(vColor.xyz * (255.0 / 128.0), vColor.w);
  if (uScale > ${lineMaxScale}) {
    gl_FragColor = texture2D(uTex, vTex) * color;
  } else {
    float dHeight = abs(vHeight) * uScale - ${lineHeight};
    color *= 1.0 - smoothstep(-0.5, 0.5, dHeight);
    gl_FragColor = color; 
  }
}
`,
  attributes: [
    ['aPos', 2, WebGLRenderingContext.FLOAT],
    ['aOffset', 2, WebGLRenderingContext.FLOAT],
    ['aTex', 2, WebGLRenderingContext.FLOAT],
    ['aHeight', 1, WebGLRenderingContext.FLOAT],
    ['aColor', 4, WebGLRenderingContext.UNSIGNED_BYTE],
    ['aFlatColor', 4, WebGLRenderingContext.UNSIGNED_BYTE]
  ] as const,
  uniforms: ['uTransform', 'uTex', 'uScale'] as const
}

const arcSource = {
  vs: `
uniform mat4 uTransform;
uniform float uScale;
attribute vec2 aCenter;
attribute vec2 aOffset;
attribute vec2 aTex;
attribute float aRadius;
attribute float aHeight;
attribute vec4 aColor;
attribute vec4 aFlatColor;
varying vec2 vTex;
varying vec4 vColor;
varying vec2 vPos;
varying float vRadius;
void main() {
  gl_Position = uTransform * vec4(aCenter + aOffset * aHeight, 0.0, 1.0);
  vTex = aTex;
  vColor = uScale > ${lineMaxScale} ? aColor : vec4(aFlatColor.xyz * aColor.xyz, aColor.w);
  vPos = aOffset * aHeight;
  vRadius = aRadius;
}
`,
  fs: `
precision highp float;
uniform highp float uScale;
varying vec2 vTex;
varying vec4 vColor;
varying vec2 vPos;
varying float vRadius;
uniform sampler2D uTex;
void main() {
  vec4 color = vec4(vColor.xyz * (255.0 / 128.0), vColor.w);
  if (uScale > ${lineMaxScale}) {
    gl_FragColor = texture2D(uTex, vTex) * color;
  } else {
    float dHeight = abs(length(vPos) - vRadius) * uScale - ${lineHeight};
    color *= 1.0 - smoothstep(-0.5, 0.5, dHeight);
    gl_FragColor = color; 
  }
}
`,
  attributes: [
    ['aCenter', 2, WebGLRenderingContext.FLOAT],
    ['aOffset', 2, WebGLRenderingContext.FLOAT],
    ['aTex', 2, WebGLRenderingContext.FLOAT],
    ['aRadius', 1, WebGLRenderingContext.FLOAT],
    ['aHeight', 1, WebGLRenderingContext.FLOAT],
    ['aColor', 4, WebGLRenderingContext.UNSIGNED_BYTE],
    ['aFlatColor', 4, WebGLRenderingContext.UNSIGNED_BYTE]
  ] as const,
  uniforms: ['uTransform', 'uTex', 'uScale'] as const
}

const circleWidth = 1.5

const circleSource = {
  vs: `
uniform mat4 uTransform;
uniform float uScale;
attribute vec2 aPos;
attribute vec2 aCenter;
attribute float aRadius;
attribute vec4 aColor;
varying vec2 vPos;
varying float vRadius;
varying vec4 vColor;
void main() {
  gl_Position = uTransform * vec4(aPos, 0.0, 1.0);
  vPos = (aPos - aCenter) * uScale;
  vRadius = aRadius * uScale;
  vColor = aColor;
}
`,
  fs: `
precision highp float;
varying vec2 vPos;
varying float vRadius;
varying vec4 vColor;
void main() {
  float dist = ${(circleWidth + 0.5).toFixed(1)} - abs(length(vPos) - vRadius);
  if (dist < 0.0) discard;
  gl_FragColor = vec4(vColor.xyz * (255.0 / 128.0), vColor.w) * smoothstep(0.0, 1.0, dist);
}
`,
  attributes: [
    ['aPos', 2, WebGLRenderingContext.FLOAT],
    ['aCenter', 2, WebGLRenderingContext.FLOAT],
    ['aRadius', 1, WebGLRenderingContext.FLOAT],
    ['aColor', 4, WebGLRenderingContext.UNSIGNED_BYTE]
  ] as const,
  uniforms: ['uTransform', 'uScale'] as const
}

export class WebGLRenderer extends Renderer {
  private context: WebGLRenderingContext

  private programs: {
    image: ProgramType<typeof imageSource>
    background: ProgramType<typeof backgroundSource>
    circle: ProgramType<typeof circleSource>
    line: ProgramType<typeof lineSource>
    arc: ProgramType<typeof arcSource>
  }

  constructor(canvas: HTMLCanvasElement) {
    super(canvas)
    const gl = canvas.getContext('webgl', {
      premultipliedAlpha: true,
      failIfMajorPerformanceCaveat: true
    })
    if (!gl) throw Error('WebGL is not supported')
    this.context = gl

    this.programs = {
      image: createProgram(gl, imageSource),
      background: createProgram(gl, backgroundSource),
      circle: createProgram(gl, circleSource),
      line: createProgram(gl, lineSource),
      arc: createProgram(gl, arcSource)
    }
  }

  protected resize() {
    this.context.viewport(0, 0, this.canvas.width, this.canvas.height)
  }

  private bundleTextures: Record<string, WebGLTexture[]> = {}

  loadBundle(name: string, bundle: BundleData) {
    if (this.loaded.has(name)) return
    super.loadBundle(name, bundle)
    if (!this.bundleTextures[name]) {
      const textures: WebGLTexture[] = []
      bundle.forEach((data, index) =>
        Object.defineProperty(textures, index, {
          get: () => {
            const texture = createTexture(this.context, data.image)
            Object.defineProperty(textures, index, { value: texture })
            return texture
          },
          configurable: true
        })
      )
      this.bundleTextures[name] = textures
    }
  }

  private buffers: DrawBuffer[] = []

  setItems(items: RenderItem[]) {
    super.setItems(items)

    const gl = this.context

    const bufferLookup: Record<number, DrawBuffer[]> = {}
    for (const buf of this.buffers) {
      bufferLookup[buf.layer] = bufferLookup[buf.layer] ?? []
      bufferLookup[buf.layer].push(buf)
      buf.size = 0
    }

    const bufferItems = new Map<DrawBuffer, RenderItem[]>()

    const processItem = (item: RenderItem) => {
      const image = this.images[item.image]
      if (!image) return
      const texture = this.bundleTextures[image.bundle]?.[image.index]
      const shader = itemShader(item.type)

      let buffer = bufferLookup[item.layer]?.find(
        (buf) => buf.texture === texture && buf.shader === shader
      )
      if (!buffer) {
        buffer = {
          layer: item.layer,
          texture,
          shader,
          buffer: gl.createBuffer()!,
          size: 0
        }
        bufferLookup[item.layer] = bufferLookup[item.layer] ?? []
        bufferLookup[item.layer].push(buffer)
        this.buffers.push(buffer)
      }
      const arr = bufferItems.get(buffer)
      if (arr) arr.push(item)
      else bufferItems.set(buffer, [item])
    }

    for (const item of items) {
      if (item.type === 'text') {
        for (const it of this.writeText(item)) {
          processItem(it)
        }
      } else {
        processItem(item)
      }
    }

    for (const [buffer, items] of bufferItems) {
      const data =
        buffer.shader === 'background'
          ? writeVerticesBackground(items, this)
          : buffer.shader === 'circle'
          ? writeVerticesCircle(items, this)
          : buffer.shader === 'line'
          ? writeVerticesLine(items, this)
          : buffer.shader === 'arc'
          ? writeVerticesArc(items, this)
          : writeVerticesImage(items, this)
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer.buffer)
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW)
      buffer.size = data.byteLength
    }

    this.buffers.sort((a, b) => a.layer - b.layer)
  }

  render(transparent?: boolean) {
    const gl = this.context

    gl.clearColor(0, 0, 0, transparent ? 0 : 1)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.disable(gl.DEPTH_TEST)
    gl.enable(gl.BLEND)

    const projMat = mat4.create()
    const { size, transform } = this.state
    const left = -transform.x / transform.scale
    const top = -transform.y / transform.scale
    const right = (size.width - transform.x) / transform.scale
    const bottom = (size.height - transform.y) / transform.scale
    mat4.ortho(projMat, left, right, bottom, top, -1, 1)

    gl.activeTexture(gl.TEXTURE0)

    let shader: ReturnType<typeof itemShader> | undefined

    for (const buf of this.buffers) {
      if (buf.layer === Layer.ARTWORK) {
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      } else if (
        buf.layer === Layer.JEWEL_RADIUS1 ||
        buf.layer === Layer.JEWEL_RADIUS2
      ) {
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE)
      } else {
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
      }

      if (buf.shader !== shader) {
        gl.useProgram(this.programs[buf.shader].program)
        switch (buf.shader) {
          case 'background':
            gl.uniformMatrix4fv(
              this.programs.background.uniforms.uTransform,
              false,
              projMat
            )
            gl.uniform4f(
              this.programs.background.uniforms.uDimensions,
              left,
              top,
              right,
              bottom
            )
            gl.uniform1i(this.programs.background.uniforms.uTex, 0)
            break
          case 'image':
            gl.uniformMatrix4fv(
              this.programs.image.uniforms.uTransform,
              false,
              projMat
            )
            gl.uniform1i(this.programs.image.uniforms.uTex, 0)
            break
          case 'circle':
            gl.uniformMatrix4fv(
              this.programs.circle.uniforms.uTransform,
              false,
              projMat
            )
            gl.uniform1f(this.programs.circle.uniforms.uScale, transform.scale)
            break
          case 'line':
            gl.uniformMatrix4fv(
              this.programs.line.uniforms.uTransform,
              false,
              projMat
            )
            gl.uniform1i(this.programs.line.uniforms.uTex, 0)
            gl.uniform1f(
              this.programs.line.uniforms.uScale,
              Math.max(transform.scale, 0.05)
            )
            break
          case 'arc':
            gl.uniformMatrix4fv(
              this.programs.arc.uniforms.uTransform,
              false,
              projMat
            )
            gl.uniform1i(this.programs.arc.uniforms.uTex, 0)
            gl.uniform1f(
              this.programs.arc.uniforms.uScale,
              Math.max(transform.scale, 0.05)
            )
            break
        }
        shader = buf.shader
      }
      if (!buf.size) continue
      const program = this.programs[buf.shader]
      gl.bindTexture(gl.TEXTURE_2D, buf.texture)
      bindVertices(gl, buf.buffer, program.attributes)
      gl.drawArrays(gl.TRIANGLES, 0, buf.size / program.vertexSize)
    }
  }
}
